import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, FileCode2, FileSpreadsheet, FileDown, Printer, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, download, errMsg, fmtDate, openBlob, todayIso } from "@/lib/api";

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString("sv-SE").slice(0, 7);
};

function DayPrint({ day, onClose }) {
  if (!day) return null;
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="day-print-dialog">
        <DialogHeader><DialogTitle>Movimentazione del {fmtDate(day.date)}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm" id="print-area">
          <table className="w-full border text-left">
            <tbody>
              <tr className="border-b"><th className="p-2">Stato esercizio</th><td className="p-2">{day.closed ? "Chiuso" : "Aperto"}</td></tr>
              <tr className="border-b"><th className="p-2">Camere disponibili</th><td className="p-2">{day.rooms_available}</td></tr>
              <tr className="border-b"><th className="p-2">Posti letto disponibili</th><td className="p-2">{day.beds_available}</td></tr>
              <tr className="border-b"><th className="p-2">Camere occupate</th><td className="p-2">{day.rooms_occupied}</td></tr>
              <tr><th className="p-2">Presenti (letti occupati)</th><td className="p-2">{day.present}</td></tr>
            </tbody>
          </table>
          <table className="w-full border text-left">
            <thead className="bg-stone-100"><tr><th className="p-2">Provenienza</th><th className="p-2">Arrivi</th><th className="p-2">Partenze</th></tr></thead>
            <tbody>
              {day.by_provenance.length === 0 && <tr><td className="p-2 text-stone-500" colSpan={3}>Nessun movimento</td></tr>}
              {day.by_provenance.map((p) => <tr key={p.provenance} className="border-t"><td className="p-2">{p.label}</td><td className="p-2">{p.arrivals}</td><td className="p-2">{p.departures}</td></tr>)}
              <tr className="border-t font-semibold"><td className="p-2">Totale</td><td className="p-2">{day.arrivals}</td><td className="p-2">{day.departures}</td></tr>
            </tbody>
          </table>
        </div>
        <DialogFooter className="no-print"><Button variant="outline" onClick={() => window.print()} data-testid="print-day-button"><Printer className="h-4 w-4 mr-2" />Stampa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransmitDialog({ open, onOpenChange, ym, onDone }) {
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const go = async () => {
    const fd = new FormData();
    fd.append("transmitted_date", date);
    fd.append("note", note);
    if (file) fd.append("proof", file);
    try {
      await api.post(`/istat/${ym}/transmitted`, fd);
      toast.success("Mese segnato come trasmesso");
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transmit-dialog">
        <DialogHeader><DialogTitle>Segna {ym} come trasmesso</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Dopo aver caricato il file su dms.puglia.it (SPOT → Carica movimentazione), registra qui la data.</p>
        <div className="space-y-1.5"><Label>Data trasmissione</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="transmit-date-input" /></div>
        <div className="space-y-1.5"><Label>Note (facoltative)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="transmit-note-input" /></div>
        <div className="space-y-1.5"><Label>Ricevuta / screenshot (facoltativo)</Label><input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0])} className="text-sm" data-testid="transmit-proof-input" /></div>
        <DialogFooter><Button onClick={go} data-testid="transmit-submit-button">Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Movimentazione() {
  const [params, setParams] = useSearchParams();
  const ym = params.get("m") || prevMonth();
  const [m, setM] = useState(null);
  const [open, setOpen] = useState({});
  const [ack, setAck] = useState(false);
  const [printDay, setPrintDay] = useState(null);
  const [tx, setTx] = useState(false);
  const monthEnd = (s) => { const [y, mo] = s.split("-").map(Number); return `${s}-${String(new Date(y, mo, 0).getDate()).padStart(2, "0")}`; };
  const [range, setRange] = useState({ from: `${ym}-01`, to: monthEnd(ym) });
  useEffect(() => {
    setRange({ from: `${ym}-01`, to: monthEnd(ym) });
  }, [ym]);
  const load = useCallback(() => {
    api.get(`/istat/${ym}`).then(({ data }) => setM(data)).catch((e) => toast.error(errMsg(e)));
    setAck(false);
  }, [ym]);
  useEffect(() => {
    load();
  }, [load]);
  const blocking = m?.issues.filter((i) => i.blocking) || [];
  const warnings = m?.issues.filter((i) => !i.blocking) || [];
  const inRange = (i) => !i.date || (i.date >= range.from && i.date <= range.to);
  const rangeBlocking = blocking.filter(inRange);
  const rangeWarnings = warnings.filter(inRange);
  const rp = { date_from: range.from, date_to: range.to };
  const rangeOk = range.from && range.to && range.from <= range.to;
  const exportFile = (fmt) => download(`/istat/export-range/${fmt}`, rp).catch((e) => toast.error(errMsg(e)));
  const exportXml = async () => {
    try {
      await download(`/istat/export-range/xml`, { ...rp, confirm_warnings: ack });
      toast.success("File XML per DMS scaricato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const jump = (date) => document.getElementById(`day-${date}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  const reopen = async () => {
    await api.post(`/istat/${ym}/reopen`);
    toast.success("Mese riaperto");
    load();
  };
  return (
    <div className="space-y-6" data-testid="movimentazione-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">ISTAT · DMS Puglia (SPOT Online)</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Movimentazione mensile</h1>
          <p className="text-sm text-stone-500 mt-1">L'app non comunica con il DMS: scarica il file e caricalo a mano su dms.puglia.it.</p>
        </div>
        <Input type="month" className="w-auto h-11" value={ym} onChange={(e) => e.target.value && setParams({ m: e.target.value })} data-testid="month-selector" />
      </div>
      {m && (
        <>
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="rounded-2xl border bg-white p-5 lg:col-span-1 space-y-2" data-testid="month-status-card">
              <StatusBadge kind="month" status={m.status} testId="month-status" />
              <p className="text-sm text-stone-600">Scadenza: <b>{fmtDate(m.deadline)}</b></p>
              {m.transmitted_at && <p className="text-sm text-emerald-700">Trasmesso il {fmtDate(m.transmitted_at)} {m.transmitted_note && `· ${m.transmitted_note}`}</p>}
              {m.has_proof && <button className="text-sm text-primary underline" onClick={() => openBlob(`/istat/${ym}/proof`)} data-testid="view-proof-button">Vedi ricevuta allegata</button>}
              {m.modified_after_export && <p className="flex gap-2 text-sm text-amber-800 bg-amber-50 rounded-lg p-2" data-testid="modified-after-export-warning"><AlertTriangle className="h-4 w-4 shrink-0" />Dati modificati dopo la trasmissione/export: riesporta e ricarica sul portale.</p>}
            </div>
            {[["Arrivi", m.totals.arrivals], ["Partenze", m.totals.departures], ["Presenze (notti)", m.totals.presenze]].map(([l, v]) => (
              <div key={l} className="rounded-2xl border bg-white p-5"><p className="font-display text-4xl font-extrabold">{v}</p><p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">{l}</p></div>
            ))}
          </div>
          <div className="rounded-2xl border bg-white p-5 space-y-3" data-testid="coherence-checks">
            <h2 className="font-display text-xl font-bold">Controlli di coerenza</h2>
            {m.issues.length === 0 && <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />Nessun problema rilevato.</p>}
            {blocking.map((i, k) => (
              <div key={`b${k}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid={`issue-blocking-${k}`}>
                <button className="text-left flex-1 underline-offset-2 hover:underline" onClick={() => jump(i.date)}><b>Bloccante:</b> {i.message}</button>
                {i.booking_id && <Link to={`/prenotazioni/${i.booking_id}`} className="font-semibold underline">Apri</Link>}
              </div>
            ))}
            {warnings.map((i, k) => (
              <div key={`w${k}`} className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`issue-warning-${k}`}><b>Avviso:</b> {i.message}</div>
            ))}
            {warnings.length > 0 && (
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="ack-warnings-checkbox" />Ho controllato gli avvisi, procedi comunque</label>
            )}
          </div>
          <div className="rounded-2xl border bg-white p-5 space-y-3" data-testid="export-range-section">
            <h2 className="font-display text-xl font-bold">Periodo da esportare</h2>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">Dal<Input type="date" className="w-auto mt-1" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="export-date-from" /></label>
              <label className="text-sm">Al<Input type="date" className="w-auto mt-1" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="export-date-to" /></label>
              <Button variant="ghost" size="sm" onClick={() => setRange({ from: `${ym}-01`, to: monthEnd(ym) })} data-testid="export-range-month">Mese intero</Button>
            </div>
            {!rangeOk && <p className="text-sm text-rose-700">La data finale deve essere uguale o successiva a quella iniziale.</p>}
            <p className="text-xs text-stone-500">Puoi scegliere anche date su più mesi. Il mese risulta "esportato" solo se il periodo lo comprende per intero.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={exportXml} disabled={!rangeOk || rangeBlocking.length > 0 || (rangeWarnings.length > 0 && !ack)} data-testid="export-xml-button"><FileCode2 className="h-4 w-4 mr-2" />Scarica file per DMS</Button>
            <Button variant="outline" disabled={!rangeOk} onClick={() => exportFile("csv")} data-testid="export-csv-button"><FileDown className="h-4 w-4 mr-2" />Esporta CSV</Button>
            <Button variant="outline" disabled={!rangeOk} onClick={() => exportFile("xlsx")} data-testid="export-xlsx-button"><FileSpreadsheet className="h-4 w-4 mr-2" />Esporta Excel</Button>
            {m.status === "esportato" && <Button variant="outline" onClick={() => setTx(true)} data-testid="mark-transmitted-button"><CheckCircle2 className="h-4 w-4 mr-2" />Segna come trasmesso</Button>}
            {["esportato", "trasmesso"].includes(m.status) && <Button variant="ghost" onClick={reopen} data-testid="reopen-month-button"><RotateCcw className="h-4 w-4 mr-2" />Riapri</Button>}
          </div>
          <div className="overflow-x-auto rounded-2xl border bg-white" data-testid="daily-table">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                <tr><th className="p-3 w-8" /><th className="p-3">Data</th><th className="p-3">Stato</th><th className="p-3">Camere occ.</th><th className="p-3">Arrivi</th><th className="p-3">Partenze</th><th className="p-3">Presenti</th><th className="p-3" /></tr>
              </thead>
              <tbody>
                {m.days.map((d) => (
                  <Fragment key={d.date}>
                    <tr id={`day-${d.date}`} className={`border-t ${d.closed ? "bg-stone-100 text-stone-500" : ""} ${m.issues.some((i) => i.date === d.date && i.blocking) ? "bg-rose-50" : ""}`} data-testid={`day-row-${d.date}`}>
                      <td className="p-3">{d.by_provenance.length > 0 && (
                        <button onClick={() => setOpen({ ...open, [d.date]: !open[d.date] })} data-testid={`expand-day-${d.date}`}>{open[d.date] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
                      )}</td>
                      <td className="p-3 font-medium whitespace-nowrap">{fmtDate(d.date)}</td>
                      <td className="p-3">{d.closed ? "Chiuso" : "Aperto"}</td>
                      <td className="p-3">{d.rooms_occupied}/{d.rooms_available}</td>
                      <td className="p-3">{d.arrivals}</td>
                      <td className="p-3">{d.departures}</td>
                      <td className="p-3">{d.present}</td>
                      <td className="p-3"><button className="text-stone-400 hover:text-primary" onClick={() => setPrintDay(d)} data-testid={`print-day-${d.date}`} aria-label="Stampa"><Printer className="h-4 w-4" /></button></td>
                    </tr>
                    {open[d.date] && d.by_provenance.map((p) => (
                      <tr key={p.provenance} className="bg-stone-50/60 text-xs text-stone-600">
                        <td /><td className="p-2 pl-6" colSpan={3}>{p.label}</td><td className="p-2">{p.arrivals}</td><td className="p-2">{p.departures}</td><td colSpan={2} />
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-2xl border bg-white p-5" data-testid="export-history">
            <h2 className="font-display text-xl font-bold mb-3">Storico export</h2>
            {m.history.length === 0 ? <p className="text-sm text-stone-500">Nessun export per questo mese.</p> : (
              <ul className="divide-y">{m.history.map((h) => (
                <li key={h.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{h.date_from ? `${fmtDate(h.date_from)} → ${fmtDate(h.date_to)} · ` : ""}Versione {h.version} · {new Date(h.created_at).toLocaleString("it-IT")} · {h.user}</span>
                  <Button size="sm" variant="ghost" onClick={() => download(`/istat/exports/${h.id}`)} data-testid={`redownload-export-${h.version}`}><FileDown className="h-4 w-4" /></Button>
                </li>
              ))}</ul>
            )}
          </div>
        </>
      )}
      <DayPrint day={printDay} onClose={() => setPrintDay(null)} />
      <TransmitDialog open={tx} onOpenChange={setTx} ym={ym} onDone={load} />
    </div>
  );
}
